import { Inject, Injectable } from "@angular/core";
import { APP_CONFIG, AppConfig } from "@src/app/app-config.module";
import { Observable } from "rxjs";
import {
    Applicant, Application, ApplicationSegment, ApplicationSource, ApplicationStartDTO, ApplicationStatus, BusinessActivitySurvey, CheckVerificationCodeRequestDTO,
    Company, CompanyType, ComplianceData, Contact, ContactRole, EMoneyLicenseIssuer, EmailVerificationData, EpaStatus,
    ExpectedActivitySurvey, IDVSessionDetails, MarketingData, UBOProvisioningChoice, UserIDVDetails
} from "../models/data-model";
import { AmplitudeService } from "./amplitude.service";
import { BundleInfoService, SoldoProductName } from "./bundle-info.service";
import { CountryService } from "./country.service";
import { GoogleTagManagerService } from "./google-tag-manager.service";
import { I18nService } from "./i18n.service";
import { IntercomIntegrationService } from "./intercomIntegration.service";
import { LoadingScreenService } from "./loading-screen.service";
import { RegistrationApiService } from "./registration-api.service";
import { SoldoCookieService } from "./soldo-cookie.service";
import { NavigationDirection, REGISTRATION_STEP_ID, RegistrationStep, StepService } from "./steps.service";

export type SoldoProductMarket = "ITA" | "GBR" | "IRL" | "ROE" | "NLD" | "DEU" | "FRA" | "SPA";

@Injectable({
    providedIn: 'root'
})
export class RegistrationService {
    private cookiePreferences: string;
    private marketingData: MarketingData;
    private countryOfIncorporation: string;
    private applicationSource: ApplicationSource;
    private applicationSegment: ApplicationSegment = ApplicationSegment.selfServe;
    private oneTimeVerificationData: CheckVerificationCodeRequestDTO;

    constructor(
        private readonly registrationApiService: RegistrationApiService,
        private readonly soldoCookieService: SoldoCookieService,
        private readonly bundleInfoService: BundleInfoService,
        private readonly stepService: StepService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly intercomIntegrationService: IntercomIntegrationService,
        private readonly i18nService: I18nService,
        @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
        private readonly countryService: CountryService,
        private readonly loadingScreenService: LoadingScreenService
    ) { }

    getApplicationId(): string {
        return this.registrationApiService.applicationId;
    }

    hasRegistrationContext(): boolean {
        return Boolean(this.getApplicationId()) && this.hasJWT();
    }

    setApplicationSource(applicationSource: ApplicationSource) {
        this.applicationSource = applicationSource;
    }

    setPlanAndOffer(plan: string, offer: string): void {
        this.bundleInfoService.setCurrentApplicationPlan(plan);
        this.bundleInfoService.setCurrentApplicationOffer(offer);
        this.amplitudeService.setPlanAndOffer(plan, offer);
    }

    setOneTimeCode(code: string): void {
        const parts = atob(code).split('|');
        this.oneTimeVerificationData = {
            email: parts[0],
            code: parts[1]
        };
    }

    getOneTimeCode(): CheckVerificationCodeRequestDTO {
        return this.oneTimeVerificationData;
    }

    setSegment(s: ApplicationSegment) {
        this.applicationSegment = s;
    }

    getSegment(): ApplicationSegment {
        return this.applicationSegment;
    }

    checkPlanAndOfferValidity(plan: string, offer: string): Promise<boolean> {
        return this.registrationApiService.checkPlanOfferValidity(plan, offer)
            .then(() => true)
            .catch(() => false);
    }

    getNavBarActivationObservable(): Observable<boolean> {
        return this.stepService.navBarActivationObservable;
    }

    deactivateProgressBar() {
        this.stepService.deactivateProgressBar();
    }

    activateProgressBar() {
        this.stepService.activateProgressBar();
    }

    isUboChoiceStepEnabled(eMoneyLicenseIssuer: EMoneyLicenseIssuer): boolean {
        return this.stepService.isUboChoiceStepEnabled(eMoneyLicenseIssuer);
    }

    isUboDetailsStepEnabled(isUboChoiceStepEnabled: boolean, uboProvisioningChoice: UBOProvisioningChoice): boolean {
        return this.stepService.isUboDetailsStepEnabled(isUboChoiceStepEnabled, uboProvisioningChoice);
    }

    setMarketingData(marketingData: MarketingData) {
        this.marketingData = marketingData;
    }

    getProductName(): SoldoProductName {
        return this.bundleInfoService.getCurrentApplicationProductName();
    }

    getPlan(): string {
        return this.bundleInfoService.getCurrentApplicationPlan();
    }

    getOffer(): string {
        return this.bundleInfoService.getCurrentApplicationOffer();
    }

    isEligibleForStartupOffer(): boolean {
        return this.getPlan() === "ProStartup";
    }

    getCountryOfIncorporation() {
        return this.countryOfIncorporation;
    }

    setJWT(accessToken: string): void {
        this.soldoCookieService.storeJWT(accessToken);
    }

    hasJWT(): boolean {
        return this.soldoCookieService.hasJWT();
    }

    deleteJWT(): void {
        this.soldoCookieService.cleanJWT();
    }

    getNextStep(): string {
        return this.stepService.getNextStep()?.linkRoute();
    }

    getPreviousStep(): string {
        return this.stepService.getPreviousStep()?.linkRoute();
    }

    selectCompanySearchInputMode(): string {
        this.stepService.setCompanyInput("search");
        return this.stepService.getCompanySearchStep()?.linkRoute();
    }

    selectManualCompanyInputMode(): string {
        this.stepService.setCompanyInput("manual");
        return this.stepService.getManualCompanyInputStep()?.linkRoute();
    }

    isStepComplete(step: REGISTRATION_STEP_ID): boolean {
        return this.stepService.isStepComplete(step);
    }

    getLastNavigationDirection(): NavigationDirection {
        return this.stepService.getLastNavigationDirection();
    }

    backToCompanies(): string {
        this.countryOfIncorporation = null;
        this.applicationSource = null;
        return 'auth/applications';
    }

    getRegistrationFlowSnapshot() {
        return this.stepService.getRegistrationFlowSnapshot();
    }

    getStepChangeObservable(): Observable<RegistrationStep> {
        return this.stepService.stepChanged;
    }

    requestEmailVerificationCode(emailVerificationData: EmailVerificationData) {
        return this.registrationApiService.requestEmailVerificationCode(emailVerificationData);
    }

    async checkVerificationCode(checkCodeRequest: CheckVerificationCodeRequestDTO) {
        const response = await this.registrationApiService.checkEmailVerificationCode(checkCodeRequest);
        this.setJWT(response.token);
        return response;
    }

    async getApplicationList(redirectWhenUnauthorized = true): Promise<Application[]> {
        const list = await this.registrationApiService.getApplicationsList(redirectWhenUnauthorized);
        return list?.filter(app => app.status !== ApplicationStatus.Abandoned);
    }

    async startApplication(data: Partial<ApplicationStartDTO>): Promise<Application> {
        data.planName = data.planName ?? this.getPlan();
        data.offerName = data.offerName ?? this.getOffer();
        const validBundle = await this.checkPlanAndOfferValidity(data.planName, data.offerName);
        if (validBundle) {
            const config = this.soldoCookieService.getStoredConfig();
            data.discountCode = config.discountCode;
            data.cookiePreferences = this.cookiePreferences;
            if (this.applicationSource) {
                data.sourceOverride = this.applicationSource;
            }
            const application = await this.registrationApiService.startApplication(data as ApplicationStartDTO)
            void this.registrationApiService.saveMarketingData(this.marketingData);
            return application;
        } else {
            const tracking = this.amplitudeService.trackBundleCodeRejected(data.planName, data.offerName);
            await this.redirectToPlanSelection(tracking.promise);
        }

    }

    // Tracks the ejection, then leaves the app. The flush must complete first: the hard
    // navigation tears the page down immediately and a fire-and-forget track() is lost.
    // .finally() so a blocked ingestion call never traps the applicant here.
    async redirectToPlanSelection(tracking: Promise<void>): Promise<void> {
        this.loadingScreenService.show();
        const redirect = (url: string) => window.location.href = url;
        const timeout = new Promise<void>((resolve) => setTimeout(() => resolve(), 3000));
        const country = this.i18nService.languagetToCountry(this.i18nService.lang);
        const baseURL = this.appConfig.invalidPlanOfferRedirectUrl[country] ?? this.appConfig.invalidPlanOfferRedirectUrl.fallback;

        return Promise.race([tracking, timeout]).finally(() => redirect(baseURL))
    }

    async getApplicant(): Promise<Applicant> {
        const applicant = await this.registrationApiService.getApplicant();
        this.intercomIntegrationService.setApplicantName(applicant.firstName, applicant.lastName);
        return applicant;
    }

    async saveApplicant(applicant: Applicant): Promise<void> {
        await this.registrationApiService.saveApplicant(applicant);
        this.intercomIntegrationService.setApplicantName(applicant.firstName, applicant.lastName);
    }

    getApplication(applicationId: string = null): Promise<Application> {
        if (applicationId) {
            this.registrationApiService.applicationId = applicationId;
        }
        return this.registrationApiService.getApplication()
            .then(app => {
                this.stepService.setApplication(app);
                this.gtmService.setExternalId(app.salesforceExternalId);
                this.setCompanyData(app.company);
                this.intercomIntegrationService.setApplicantName(app.applicant?.firstName, app.applicant?.lastName);
                return app;
            });
    }

    private setCompanyData(company: Company) {
        if (company) {
            this.stepService.setCompany(company);
            this.countryOfIncorporation = company?.registeredAddress?.countryISO;
            if (this.countryOfIncorporation) {
                this.gtmService.setCountryOfIncorporation(this.countryOfIncorporation);
                this.gtmService.setProductMarket(this.getProductMarket());
            }
            this.gtmService.setCompanySize(company.employees);
            this.gtmService.setCompanyType(company.type);
        }
    }

    saveApplication(application: Application): Promise<void> {
        return this.registrationApiService.saveApplication(application)
            .then(() => {
                this.setCompanyData(application.company);
                this.stepService.setApplication(application);
                this.intercomIntegrationService.setApplicantName(application.applicant?.firstName, application.applicant?.lastName);
            });
    }

    setCookiePreferences(value: string) {
        this.cookiePreferences = value;
    }

    getCompany(): Promise<Company> {
        return this.registrationApiService.getCompany()
            .then((company) => {
                this.setCompanyData(company);
                return company;
            });
    }

    saveCompany(company: Company): Promise<void> {
        return this.registrationApiService.saveCompany(company)
            .then(() => {
                this.setCompanyData(company);
                this.stepService.setCompany(company);
            });
    }

    getBusinessActivitySurvey(): Promise<BusinessActivitySurvey> {
        return this.registrationApiService.getBusinessActivitySurvey();
    }

    saveBusinessActivitySurvey(survey: BusinessActivitySurvey): Promise<void> {
        return this.registrationApiService.saveBusinessActivitySurvey(survey);
    }

    getAllContacts(): Promise<Contact[]> {
        return this.registrationApiService.getContacts()
            .then(contacts => {
                const applicant = contacts.find(c => c.roles.includes(ContactRole.Applicant));
                this.intercomIntegrationService.setApplicantName(applicant?.firstName, applicant?.lastName);
                return contacts;
            });
    }

    saveAllContacts(contacts: Contact[]) {
        return this.registrationApiService.saveAllContacts(contacts)
            .then(() => {
                const applicant = contacts.find(c => c.roles.includes(ContactRole.Applicant));
                this.intercomIntegrationService.setApplicantName(applicant?.firstName, applicant?.lastName);
                return contacts;
            });
    }

    getExpectedActivitySurvey() {
        return this.registrationApiService.getExpectedActivitySurvey();
    }

    saveExpectedActivitySurvey(survey: ExpectedActivitySurvey) {
        return this.registrationApiService.saveExpectedActivitySurvey(survey);
    }

    async initRegistration(application: Application): Promise<string> {
        this.stepService.activateProgressBar();
        this.stepService.setApplication(application);
        this.registrationApiService.applicationId = application.id;
        this.gtmService.setExternalId(application.salesforceExternalId);
        this.setPlanAndOffer(application.planName, application.offerName);
        const webappChecklist = application.webappCheckList?.split(';') || [];
        this.amplitudeService.setWebappChecklist(webappChecklist);
        this.applicationSegment = application.segment;
        this.setCompanyData(application.company);
        this.intercomIntegrationService.setApplicantName(application.applicant?.firstName, application.applicant?.lastName);
        this.intercomIntegrationService.setSalesforceExternalId(application.salesforceExternalId);
        this.amplitudeService.setSalesforceExternalId(application.salesforceExternalId);
        let complianceData: ComplianceData;
        if ([ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress, ApplicationStatus.Pending].includes(application.status)) {
            complianceData = await this.getComplianceData();
        }
        await this.initAuthorizedSpace();
        return this.stepService.initRegistrationFlow(application, complianceData);
    }

    async completeApplication(discountCode?: string): Promise<ComplianceData> {
        const complianceData = await this.registrationApiService.completeApplication(discountCode);
        this.stepService.setComplianceData(complianceData);
        const application = await this.registrationApiService.getApplication();
        this.stepService.setApplication(application);
        this.amplitudeService.trackApplicationCompleted();
        this.gtmService.trackRegistrationCompleted(complianceData.status);
        this.gtmService.trackEKYCPassed();
        return complianceData;
    }

    getComplianceData(appId?: string): Promise<ComplianceData> {
        return this.registrationApiService.getComplianceData(appId);
    }

    getEpaStatus(appId?: string): Promise<EpaStatus> {
        return this.registrationApiService.getEpaStatus(appId);
    }

    getIdvSession(personExternalId: string): Promise<IDVSessionDetails> {
        return this.registrationApiService.getIdvSession(personExternalId);
    }

    postUserIdvData(userKycSessionId: string, data: UserIDVDetails): Promise<string> {
        return this.registrationApiService.postUserIdvDetails(data, userKycSessionId);
    }

    completeStep(completedStep: REGISTRATION_STEP_ID) {
        this.stepService.completeStep(completedStep);
        const checklist = this.stepService.getCompletedSteps();
        this.amplitudeService.setWebappChecklist(checklist);
        void this.registrationApiService.saveWebappChecklist(checklist.join(";"));
    }

    saveAndContinueLater(lang: string): Promise<void> {
        return this.registrationApiService.saveAndContinueLater(lang);
    }

    getCompanyTypes(country: string): Promise<CompanyType[]> {
        return this.registrationApiService.getCompanyTypes(country);
    }

    private async initAuthorizedSpace(): Promise<void> {
        await this.countryService.preloadMetadata();
    }

    getProductMarket(): SoldoProductMarket {
        const countryISO = this.countryOfIncorporation;
        if (countryISO === null) {
            return null;
        }
        let productMarket: SoldoProductMarket;
        switch (countryISO) {
            case "ITA":
            case "IRL":
            case "NLD":
            case "DEU":
            case "FRA":
                productMarket = countryISO;
                break;
            case "GIB":
            case "GBR":
                productMarket = "GBR";
                break;
            default:
                productMarket = "ROE";
        }

        return productMarket;
    }

}
